import { createBrowserRouter, Navigate } from "react-router-dom";
import { RequireRole } from "@/features/auth/RequireRole";
import { LoginPage } from "@/features/auth/LoginPage";
import { AppShell } from "./layout/AppShell";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { APP_ROLES, ADMIN_ROLES } from "@/shared/types/role";

import { HelpPage } from "@/features/help/HelpPage";
import { AssistantSettingsPage } from "@/features/assistant/AssistantSettingsPage";
import { AiProfilePage } from "@/features/ai-profile/AiProfilePage";
import { CampaignsPage } from "@/features/campaigns/CampaignsPage";
import { LeadProfilePage } from "@/features/campaigns/leads/LeadProfilePage";
import { NewCampaignPage } from "@/features/campaigns/NewCampaignPage";
import { CampaignLayout } from "@/features/campaigns/CampaignLayout";
import { OverviewTab } from "@/features/campaigns/tabs/OverviewTab";
import { LeadsTab } from "@/features/campaigns/tabs/LeadsTab";
import { AnalyticsTab } from "@/features/campaigns/tabs/AnalyticsTab";
import { DiscoveryTab } from "@/features/campaigns/tabs/DiscoveryTab";
import { EnrichmentTab } from "@/features/campaigns/tabs/EnrichmentTab";
import { AudienceTab } from "@/features/campaigns/tabs/AudienceTab";
import { SequencesTab } from "@/features/campaigns/tabs/SequencesTab";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import {
  DiscoveryPage,
  LeadsPage,
  SequencesPage,
  OutboxPage,
  AnalyticsPage,
} from "@/features/global/GlobalPages";
import { TasksPage } from "@/features/tasks/TasksPage";
import { TaskDetailPage } from "@/features/tasks/TaskDetailPage";

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  { path: "/403", element: <ForbiddenPage /> },
  {
    path: "/",
    // Every authenticated Growth user reaches the shell; finer role gates live
    // on the tenant-management surfaces (added with the users feature).
    element: (
      <RequireRole roles={APP_ROLES}>
        <AppShell />
      </RequireRole>
    ),
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      // Global (cross-campaign) nav
      { path: "dashboard", element: <DashboardPage /> },
      { path: "campaigns", element: <CampaignsPage /> },
      { path: "campaigns/new", element: <NewCampaignPage /> },
      { path: "discovery", element: <DiscoveryPage /> },
      { path: "leads", element: <LeadsPage /> },
      { path: "leads/:id", element: <LeadProfilePage /> },
      { path: "sequences", element: <SequencesPage /> },
      { path: "tasks", element: <TasksPage /> },
      { path: "tasks/:id", element: <TaskDetailPage /> },
      { path: "outbox", element: <OutboxPage /> },
      { path: "analytics", element: <AnalyticsPage /> },
      { path: "help", element: <HelpPage /> },
      { path: "assistant/settings", element: <AssistantSettingsPage /> },
      {
        // Admin surface: brand & AI customization (ADR-0018). MARKETING doesn't configure.
        path: "ai-profile",
        element: (
          <RequireRole roles={ADMIN_ROLES}>
            <AiProfilePage />
          </RequireRole>
        ),
      },

      // Campaign-scoped pipeline (tab order = pipeline order)
      {
        path: "campaigns/:id",
        element: <CampaignLayout />,
        children: [
          { index: true, element: <OverviewTab /> },
          { path: "discovery", element: <DiscoveryTab /> },
          { path: "leads", element: <LeadsTab /> },
          { path: "enrichment", element: <EnrichmentTab /> },
          { path: "audience", element: <AudienceTab /> },
          { path: "sequences", element: <SequencesTab /> },
          { path: "analytics", element: <AnalyticsTab /> },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
